import { Link, useLocation } from "wouter";
import { Bell, Settings, User, LogOut } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { LanguageSwitcher } from "../language-switcher";
import { GlobalSearch } from "../shared/global-search";
import { useAuth } from "../../hooks/use-auth";
import { NotificationsDropdown } from "../notifications/notifications-dropdown";

const navLinks = [
  { href: "/dashboard", label: "Dashboard" },
  { href: "/calendar", label: "Calendar" },
  { href: "/clients", label: "Clients" },
  { href: "/estimates", label: "Estimates" },
  { href: "/invoices", label: "Invoices" },
  { href: "/projects", label: "Projects" },
  { href: "/materials", label: "Materials" },
  { href: "/timeclock", label: "Time Clock" },
  { href: "/ai-assistant", label: "AI Assistant" },
  { href: "/settings", label: "Settings" },
];

export default function TopNav() {
  const [location] = useLocation();
  const { user, logoutMutation } = useAuth();

  const getInitials = () => {
    if (!user) return "";
    return `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`;
  };

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  return (
    <header className="remodra-topnav fixed top-0 left-0 md:left-64 right-0 z-30 border-b border-slate-300 dark:border-slate-700">
      <div className="flex items-center justify-between h-14 md:h-16 px-3 sm:px-4 md:px-6 lg:px-8 gap-2">
        {/* Left side - Global Search (hidden on mobile, shown on tablet+) */}
        <div className="hidden sm:flex flex-1 max-w-lg">
          <GlobalSearch />
        </div>

        {/* Right side actions */}
        <div className="flex items-center gap-2 sm:gap-2.5 md:gap-3 ml-auto">
          {/* Notifications - Always visible */}
          <NotificationsDropdown />

          {/* Language Switcher - Always visible, icon only on mobile */}
          <LanguageSwitcher variant="ghost" showText={false} size="icon" />

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="relative h-9 w-9 sm:h-9 sm:w-9 md:h-10 md:w-10 rounded-full p-0 hover:ring-2 hover:ring-green-500/50 transition-all flex-shrink-0"
                aria-label="User menu"
              >
                <Avatar className="h-9 w-9 sm:h-9 sm:w-9 md:h-10 md:w-10">
                  <AvatarImage src="" alt={user?.firstName} />
                  <AvatarFallback className="bg-gradient-to-br from-green-500 to-green-700 text-white font-bold text-xs sm:text-sm">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 w-56" align="end" forceMount>
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none text-slate-900 dark:text-slate-200">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs leading-none text-slate-600 dark:text-slate-400">
                    @{user?.username || user?.email}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-slate-900 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">
                <User className="mr-2 h-4 w-4" />
                <span>Profile</span>
              </DropdownMenuItem>
              <DropdownMenuItem className="text-slate-900 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">
                <Settings className="mr-2 h-4 w-4" />
                <span>Settings</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
                onClick={handleLogout}
              >
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
} 